import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type ListDoorAccessEventsFilters,
  type UpdateDoorDeviceInput,
  type UpdateIntegrationSettingsInput,
  integrationApi,
} from '../integration';

export const integrationKeys = {
  all: ['integration'] as const,
  devices: ['integration', 'devices'] as const,
  settings: ['integration', 'settings'] as const,
  agentStatus: ['integration', 'agent-status'] as const,
  bellSchedule: (date: string) => ['integration', 'bell-schedule', date] as const,
  events: (filters: ListDoorAccessEventsFilters) => ['integration', 'events', filters] as const,
  unmatchedSwipes: ['integration', 'unmatched-swipes'] as const,
};

// ---- Devices ----------------------------------------------------------

export function useIntegrationDevicesQuery() {
  return useQuery({
    queryKey: integrationKeys.devices,
    queryFn: () => integrationApi.listDevices(),
  });
}

export function useCreateDoorDeviceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof integrationApi.createDevice>[0]) => integrationApi.createDevice(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.devices }),
  });
}

export function useUpdateDoorDeviceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDoorDeviceInput }) => integrationApi.updateDevice(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.devices }),
  });
}

export function useDeleteDoorDeviceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => integrationApi.deleteDevice(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.devices }),
  });
}

// ---- Settings -----------------------------------------------------------

export function useIntegrationSettingsQuery() {
  return useQuery({
    queryKey: integrationKeys.settings,
    queryFn: () => integrationApi.getSettings(),
  });
}

export function useUpdateIntegrationSettingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateIntegrationSettingsInput) => integrationApi.updateSettings(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.settings }),
  });
}

// ---- Agent status -------------------------------------------------------

// Polled every 5 minutes so the panel stays roughly live without hammering the API;
// `refetch` (wired to a manual Refresh button) covers the "check right now" case.
export function useAgentStatusQuery() {
  return useQuery({
    queryKey: integrationKeys.agentStatus,
    queryFn: () => integrationApi.getAgentStatus(),
    refetchInterval: 5 * 60 * 1000,
  });
}

// ---- Bell day schedule ----------------------------------------------------

export function useBellScheduleQuery(date: string) {
  return useQuery({
    queryKey: integrationKeys.bellSchedule(date),
    queryFn: () => integrationApi.getBellSchedule(date),
    enabled: Boolean(date),
    // Stepping through days should not blank the list between fetches.
    placeholderData: keepPreviousData,
  });
}

// ---- API key --------------------------------------------------------

// On-demand GETs (not auto-fetched queries) so the key only leaves the server when an
// admin explicitly clicks Reveal — `useMutation` here is just "fetch on click".
export function useRevealApiKeyMutation() {
  return useMutation({
    mutationFn: () => integrationApi.revealApiKey(),
  });
}

export function useRegenerateApiKeyMutation() {
  return useMutation({
    mutationFn: () => integrationApi.regenerateApiKey(),
  });
}

// ---- Events -----------------------------------------------------------

/** The badges the panel keeps sending that resolve to nobody — a data fault, not a swipe. */
export function useUnmatchedSwipesQuery() {
  return useQuery({
    queryKey: integrationKeys.unmatchedSwipes,
    queryFn: () => integrationApi.getUnmatchedSwipes(),
  });
}

export function useIntegrationEventsQuery(filters: ListDoorAccessEventsFilters) {
  return useQuery({
    queryKey: integrationKeys.events(filters),
    queryFn: () => integrationApi.listEvents(filters),
    placeholderData: keepPreviousData,
  });
}
